// Voice & video over a WebRTC mesh; the game WebSocket carries signaling.
import { G, createStore } from "./store";
import { send, onRtc, toast } from "./net";

export interface VoiceState {
  joined: boolean;
  video: boolean;
  muted: boolean;
  local: MediaStream | null;
  streams: Record<string, MediaStream>;
}
export const V = createStore<VoiceState>({ joined: false, video: false, muted: false, local: null, streams: {} });

const ICE: RTCConfiguration = { iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }] };
const peers = new Map<string, RTCPeerConnection>();
const pendingIce = new Map<string, RTCIceCandidateInit[]>();

function newPeer(uid: string): RTCPeerConnection {
  const pc = new RTCPeerConnection(ICE);
  peers.set(uid, pc);
  const local = V.get().local;
  if (local) for (const t of local.getTracks()) pc.addTrack(t, local);
  pc.onicecandidate = e => e.candidate && send({ t: "rtc", to: uid, data: { ice: e.candidate.toJSON() } });
  pc.ontrack = e => {
    const s = e.streams[0] || new MediaStream([e.track]);
    V.set({ streams: { ...V.get().streams, [uid]: s } });
  };
  pc.onconnectionstatechange = () => {
    if (pc.connectionState === "failed" || pc.connectionState === "closed") dropPeer(uid);
  };
  return pc;
}

function dropPeer(uid: string) {
  const pc = peers.get(uid);
  peers.delete(uid);
  pendingIce.delete(uid);
  try { pc?.close(); } catch {}
  const s = { ...V.get().streams };
  delete s[uid];
  V.set({ streams: s });
}

async function call(uid: string) {
  const pc = newPeer(uid);
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  send({ t: "rtc", to: uid, data: { sdp: pc.localDescription } });
}

let unsub: (() => void) | null = null;
let unsubPresence: (() => void) | null = null;

export async function joinVoice(video: boolean) {
  if (V.get().joined) return;
  let local: MediaStream;
  try {
    local = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: video ? { width: 320, height: 240 } : false });
  } catch (e: any) {
    toast(`Couldn't access ${video ? "camera/microphone" : "microphone"}: ${e.message || e}`);
    return;
  }
  V.set({ joined: true, video, local, muted: false });
  unsub = onRtc(async (from, data) => {
    if (!V.get().joined) return;
    let pc = peers.get(from);
    if (data.sdp) {
      if (data.sdp.type === "offer") {
        if (pc) dropPeer(from);
        pc = newPeer(from);
        await pc.setRemoteDescription(data.sdp);
        const ans = await pc.createAnswer();
        await pc.setLocalDescription(ans);
        send({ t: "rtc", to: from, data: { sdp: pc.localDescription } });
      } else if (pc) await pc.setRemoteDescription(data.sdp);
      for (const c of pendingIce.get(from) || []) await pc!.addIceCandidate(c).catch(() => {});
      pendingIce.delete(from);
    } else if (data.ice) {
      if (pc?.remoteDescription) await pc.addIceCandidate(data.ice).catch(() => {});
      else pendingIce.set(from, [...(pendingIce.get(from) || []), data.ice]);
    }
  });
  // whoever has the lower uid places the call, so two peers never offer at once
  const me = G.get().me;
  const sync = () => {
    const now = new Set(G.get().online.filter(p => p.voice && p.uid !== me).map(p => p.uid));
    for (const u of [...peers.keys()]) if (!now.has(u)) dropPeer(u);
    for (const u of now) if (!peers.has(u) && me < u) call(u);
  };
  let last = "";
  unsubPresence = G.subscribe(() => {
    const key = G.get().online.filter(p => p.voice).map(p => p.uid).sort().join(",");
    if (key !== last) { last = key; sync(); }
  });
  send({ t: "voice", on: true, video });
}

export function leaveVoice() {
  if (!V.get().joined) return;
  unsub?.();
  unsubPresence?.();
  for (const u of [...peers.keys()]) dropPeer(u);
  V.get().local?.getTracks().forEach(t => t.stop());
  V.set({ joined: false, local: null, streams: {}, video: false });
  send({ t: "voice", on: false });
}

export function toggleMute() {
  const m = !V.get().muted;
  V.get().local?.getAudioTracks().forEach(t => (t.enabled = !m));
  V.set({ muted: m });
}
